import { useState } from 'react';
import { Badge, Button, Card, EmptyState, LoadingList, SectionHeader } from '@timely/ui';
import { holds } from '@timely/domain';
import { useActiveWorkspace } from '../workspace/WorkspaceProvider';
import { Page } from '../workspace/WorkspaceShell';
import { FormNotice } from './AuthLayout';
import { useTeam } from '../team/useTeam';
import {
    MappingError, applyReconciliation, previewReconciliation, removeOwnerMapping, setOwnerMapping,
    useOwnerCodesSummary, type ApplyResult, type ReconciliationCandidate,
} from '../reconciliation/useReconciliation';

/**
 * Ownership reconciliation.
 *
 * An Admin/Manager setup screen, deliberately away from the daily collector
 * workflow — configuring what an imported code means is `team.manage`;
 * previewing and applying what that means for the book is
 * `customers.assign`. Neither step moves an account by itself: a mapping
 * saved here changes nothing until Apply is pressed, on exactly the accounts
 * the preview showed, and never on one that has changed since.
 */
export function Reconciliation() {
    const context = useActiveWorkspace();
    const { workspace } = context;
    const canMap = holds(context, 'team.manage');
    const canReconcile = holds(context, 'customers.assign');

    const { codes, loading, error, reload } = useOwnerCodesSummary(workspace.id);
    const { members } = useTeam(context);
    const activeMembers = members.filter(m => m.status === 'active');

    const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
    const [busyCode, setBusyCode] = useState<string | null>(null);
    const [candidates, setCandidates] = useState<ReconciliationCandidate[] | null>(null);
    const [checked, setChecked] = useState<Set<string>>(new Set());
    const [previewing, setPreviewing] = useState(false);
    const [applying, setApplying] = useState(false);
    const [results, setResults] = useState<ApplyResult[] | null>(null);

    if (!canMap && !canReconcile) {
        return (
            <Page>
                <SectionHeader title="Ownership reconciliation" />
                <Card className="mt-5"><EmptyState title="Nothing to show you here" hint="Ask an admin about mapping imported ownership codes." /></Card>
            </Page>
        );
    }

    const onMap = async (code: string, memberId: string) => {
        setBusyCode(code);
        setNotice(null);
        try {
            await setOwnerMapping(workspace.id, code, memberId);
            await reload();
        } catch (err) {
            setNotice({ tone: 'error', text: err instanceof MappingError ? err.message : 'That did not work.' });
        } finally {
            setBusyCode(null);
        }
    };

    const onUnmap = async (code: string) => {
        setBusyCode(code);
        setNotice(null);
        try {
            await removeOwnerMapping(workspace.id, code);
            await reload();
        } catch (err) {
            setNotice({ tone: 'error', text: err instanceof MappingError ? err.message : 'That did not work.' });
        } finally {
            setBusyCode(null);
        }
    };

    const runPreview = async () => {
        setPreviewing(true);
        setNotice(null);
        setResults(null);
        try {
            const rows = await previewReconciliation(workspace.id);
            setCandidates(rows);
            setChecked(new Set(rows.map(r => r.customerId)));
        } catch (err) {
            setNotice({ tone: 'error', text: err instanceof MappingError ? err.message : 'That did not work.' });
        } finally {
            setPreviewing(false);
        }
    };

    const runApply = async () => {
        if (!candidates) return;
        const chosen = candidates.filter(c => checked.has(c.customerId));
        if (chosen.length === 0) return;
        setApplying(true);
        setNotice(null);
        try {
            const outcome = await applyReconciliation(workspace.id, chosen);
            setResults(outcome);
            setCandidates(null);
            await reload();
        } catch (err) {
            setNotice({ tone: 'error', text: err instanceof MappingError ? err.message : 'That did not work.' });
        } finally {
            setApplying(false);
        }
    };

    const counted = (status: ApplyResult['status']) => (results ?? []).filter(r => r.status === status).length;

    return (
        <Page>
            <SectionHeader
                title="Ownership reconciliation"
                subtitle="What an imported owner code means, and — separately — applying it to the accounts that carry it."
            />

            {notice && <div className="mt-4"><FormNotice tone={notice.tone}>{notice.text}</FormNotice></div>}
            {error && <div className="mt-4"><FormNotice tone="error">{error}</FormNotice></div>}

            <Card className="mt-5 overflow-hidden">
                {loading ? (
                    <LoadingList label="Loading ownership codes" rows={4} />
                ) : codes.length === 0 ? (
                    <EmptyState
                        title="No imported owner codes yet"
                        hint="Once your book carries a code from a synced sheet, it appears here to map."
                    />
                ) : (
                    // Scrolls sideways inside the card on a phone rather than
                    // cutting off the mapping controls at the right.
                    <div className="overflow-x-auto">
                    <table className="w-full text-[13px]">
                        <thead>
                            <tr className="text-left text-label-3 text-[12px]">
                                <th className="font-semibold px-5 py-2.5">Imported code</th>
                                <th className="font-semibold px-3 py-2.5">Accounts</th>
                                <th className="font-semibold px-3 py-2.5">Mapped to</th>
                                {canMap && <th className="font-semibold px-5 py-2.5 text-right">&nbsp;</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-separator">
                            {codes.map(c => (
                                <tr key={c.importedCode}>
                                    <td className="px-5 py-3 font-mono font-semibold text-label">{c.importedCode}</td>
                                    <td className="px-3 py-3 num tabular-nums">{c.customerCount}</td>
                                    <td className="px-3 py-3">
                                        {c.mappedMemberId ? (
                                            <>
                                                {c.mappedMemberName}
                                                {c.mappedMemberStatus !== 'active' && <Badge tone="warn" className="ml-2">not active</Badge>}
                                            </>
                                        ) : c.suggestedMemberId ? (
                                            <span className="text-label-3">
                                                Suggested: {c.suggestedMemberName}
                                            </span>
                                        ) : (
                                            <span className="text-label-3">Not mapped</span>
                                        )}
                                    </td>
                                    {canMap && (
                                        <td className="px-5 py-3 text-right">
                                            <div className="inline-flex items-center gap-1.5">
                                                <select
                                                    aria-label={`Who ${c.importedCode} is`}
                                                    disabled={busyCode === c.importedCode}
                                                    value={c.mappedMemberId ?? ''}
                                                    onChange={e => { if (e.target.value) void onMap(c.importedCode, e.target.value); }}
                                                    className="h-9 rounded-lg bg-card-3 px-2 text-[12.5px] outline-none"
                                                >
                                                    <option value="">
                                                        {c.suggestedMemberId ? `Use suggestion (${c.suggestedMemberName})` : 'Choose a member…'}
                                                    </option>
                                                    {activeMembers.map(m => (
                                                        <option key={m.memberId} value={m.memberId}>{m.displayName}</option>
                                                    ))}
                                                </select>
                                                {c.suggestedMemberId && !c.mappedMemberId && (
                                                    <Button variant="secondary" size="sm" disabled={busyCode === c.importedCode}
                                                        onClick={() => void onMap(c.importedCode, c.suggestedMemberId!)}>
                                                        Use
                                                    </Button>
                                                )}
                                                {c.mappedMemberId && (
                                                    <Button variant="secondary" size="sm" disabled={busyCode === c.importedCode}
                                                        onClick={() => void onUnmap(c.importedCode)}>
                                                        Unmap
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                )}
            </Card>

            {canReconcile && (
                <>
                    <SectionHeader
                        title="Reconcile"
                        subtitle="Nothing moves until you review the accounts below and press Apply."
                        className="mt-8"
                        actions={
                            <Button variant="secondary" disabled={previewing} onClick={() => { void runPreview(); }}>
                                {previewing ? 'Checking…' : 'Preview'}
                            </Button>
                        }
                    />

                    {results && (
                        <Card className="mt-3 p-5">
                            <p className="text-[14px] font-semibold text-label mb-2">Applied</p>
                            <div className="flex flex-wrap gap-2 text-[13px]">
                                <Badge tone="pos">{counted('applied')} applied</Badge>
                                <Badge tone="warn">{counted('conflicted')} conflicted — changed since preview</Badge>
                                <Badge tone="neutral">{counted('skipped_unmapped')} skipped — no active mapping</Badge>
                                <Badge tone="neutral">{counted('unchanged')} already correct</Badge>
                            </div>
                        </Card>
                    )}

                    {candidates && (
                        <Card className="mt-3 overflow-hidden">
                            {candidates.length === 0 ? (
                                <EmptyState title="Nothing to reconcile" hint="Every mapped code already matches its accounts." />
                            ) : (
                                <>
                                    <div className="overflow-x-auto">
                                    <table className="w-full text-[13px]">
                                        <thead>
                                            <tr className="text-left text-label-3 text-[12px]">
                                                <th className="font-semibold px-5 py-2.5">
                                                    <label className="inline-flex p-2 -m-2 cursor-pointer">
                                                        <input
                                                            type="checkbox" className="h-5 w-5"
                                                            aria-label="Select every account listed"
                                                            checked={checked.size === candidates.length}
                                                            onChange={e => setChecked(e.target.checked ? new Set(candidates.map(c => c.customerId)) : new Set())}
                                                        />
                                                    </label>
                                                </th>
                                                <th className="font-semibold px-3 py-2.5">Account</th>
                                                <th className="font-semibold px-3 py-2.5">Code</th>
                                                <th className="font-semibold px-3 py-2.5">Current owner</th>
                                                <th className="font-semibold px-5 py-2.5">Proposed owner</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-separator">
                                            {candidates.map(c => (
                                                <tr key={c.customerId}>
                                                    <td className="px-5 py-2.5">
                                                        <label className="inline-flex p-2 -m-2 cursor-pointer">
                                                            <input
                                                                type="checkbox" className="h-5 w-5" checked={checked.has(c.customerId)}
                                                                aria-label={`Select ${c.company}`}
                                                                onChange={e => setChecked(prev => {
                                                                    const next = new Set(prev);
                                                                    if (e.target.checked) next.add(c.customerId); else next.delete(c.customerId);
                                                                    return next;
                                                                })}
                                                            />
                                                        </label>
                                                    </td>
                                                    <td className="px-3 py-2.5 text-label">{c.company}</td>
                                                    <td className="px-3 py-2.5 font-mono">{c.importedCode}</td>
                                                    <td className="px-3 py-2.5 text-label-3">{c.currentOwnerName ?? '— unassigned —'}</td>
                                                    <td className="px-5 py-2.5 font-semibold text-label">{c.proposedOwnerName}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    </div>
                                    <div className="px-5 py-3 border-t border-separator">
                                        <Button variant="primary" disabled={applying || checked.size === 0} onClick={() => { void runApply(); }}>
                                            {applying ? 'Applying…' : `Apply to ${checked.size} account${checked.size === 1 ? '' : 's'}`}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </Card>
                    )}
                </>
            )}
        </Page>
    );
}
